/**
 * ChangelogRepository — HTTP access for the customer-facing changelog.
 *
 * Endpoints:
 *   GET  /changelog          → { releases, versions, unread, show_dialog }
 *   POST /changelog/seen     → { last_seen_changelog }
 *   GET  /public/changelog   → same shape, entitlement-gated entries removed
 *
 * The public variant is a separate backend route, not the same one called
 * without a token: the backend decides what an anonymous caller may see. It
 * also uses a bare axios client with no interceptors, following
 * PublicWaterRiskRepository — the shared clients refresh on 401 and redirect to
 * /login, and a visitor reading the API docs must never be bounced to a login
 * screen.
 */
import { getApiBaseUrl } from '@/app/runtime';
import Client from '@/repositories/clients/AuthenticatedClient';
import normalizeBaseDomain from '@/repositories/clients/normalizeBaseDomain';
import axios from 'axios';

const CHANGELOG_PATH = '/changelog';
const SEEN_PATH = '/changelog/seen';
const PUBLIC_PATH = '/public/changelog';

const publicClient = axios.create({ baseURL: normalizeBaseDomain(getApiBaseUrl()) });

export default {
  /**
   * The changelog for the signed-in user, filtered to their entitlements.
   * @param {{ surface?: string, limit?: number }} [params]
   */
  getChangelog(params = {}) {
    return Client.get(CHANGELOG_PATH, { params });
  },

  /**
   * Record which versions the user has now seen.
   * @param {Object<string, string>} [versions] - omit to mark everything seen.
   */
  markSeen(versions) {
    return Client.post(SEEN_PATH, { versions: versions || {} });
  },

  /**
   * The changelog for anonymous visitors (API documentation).
   * @param {{ surface?: string, limit?: number }} [params]
   */
  getPublicChangelog(params = {}) {
    return publicClient.get(PUBLIC_PATH, { params });
  },
};
